import { ApiHttpClient } from "@/core/common/http/api-http-client";
import { HttpRequest } from "@/core/common/http/http-request";
import { ResourceBaseService } from "@/core/common/resource-base/resource-base.service";
import { PayrollDto } from "@/core/features/payrolls/payroll.dto";
import { CreatePayrollDto } from "@/core/features/payrolls/create-payroll.dto";
import { UpdatePayrollDto } from "@/core/features/payrolls/update-payroll.dto";
import { CalculatePayrollResultDto } from "@/core/features/payrolls/calculate-payroll-result.dto";
import { PayrollCalendarViewDto } from "@/core/features/payrolls/payroll-calendar-view.dto";
import { plainToInstance } from "class-transformer";
import { IRequest } from "@/interfaces/IRequest";
import { QueryRequestHelper } from "@/core/common/resource-base/query-request.helper";
import { WorksheetsStatusByOperativeUnitDto } from "@/core/features/payrolls/worksheets-status-by-operative-unit.dto";
import { OnCallDto } from "@/core/features/on-calls/on-call.dto";

export class PayrollsResource extends ResourceBaseService<PayrollDto, CreatePayrollDto, UpdatePayrollDto> {
  protected EntityDto = PayrollDto;
  protected CreateEntityDto = CreatePayrollDto;
  protected UpdateEntityDto = UpdatePayrollDto;

  constructor(protected readonly apiHttpClient: ApiHttpClient) {
    super(apiHttpClient, "/payrolls");
  }

  generatePreviousMonth(payload?: { monthsAgo?: 0 | 1 }): Promise<{ jobId: string }> {
    const searchParams = new URLSearchParams();
    if (payload?.monthsAgo != null) searchParams.set("monthsAgo", payload.monthsAgo.toString());
    const query: string = searchParams.toString();

    return this.apiHttpClient.request<null, { jobId: string }>(
      HttpRequest.create({
        url: this.resourceUrl + "/generate-previous-month",
        method: "POST",
        auth: true,
        query: query.length ? query : null,
      })
    );
  }

  calculatePayroll(payrollId: string): Promise<CalculatePayrollResultDto> {
    return this.apiHttpClient
      .request<null, CalculatePayrollResultDto>(
        HttpRequest.create({
          url: `${this.resourceUrl}/${payrollId}/calculate`,
          method: "GET",
          auth: true,
        })
      )
      .then((result: CalculatePayrollResultDto) => plainToInstance(CalculatePayrollResultDto, result));
  }

  getPayrollCalendarView(payrollId: string): Promise<PayrollCalendarViewDto> {
    return this.apiHttpClient
      .request<null, PayrollCalendarViewDto>(
        HttpRequest.create({
          url: `${this.resourceUrl}/${payrollId}/calendar-view`,
          method: "GET",
          auth: true,
        })
      )
      .then((result: PayrollCalendarViewDto) => plainToInstance(PayrollCalendarViewDto, result));
  }

  getPayrollOnCalls(payrollId: string): Promise<OnCallDto[]> {
    return this.apiHttpClient
      .request<null, OnCallDto[]>(
        HttpRequest.create({
          url: `${this.resourceUrl}/${payrollId}/on-calls`,
          method: "GET",
          auth: true,
        })
      )
      .then((result: OnCallDto[]) => plainToInstance(OnCallDto, result));
  }

  recalculatePayroll(payrollId: string): Promise<{ jobId: string }> {
    return this.apiHttpClient.request<null, { jobId: string }>(
      HttpRequest.create({
        url: `${this.resourceUrl}/${payrollId}/recalculate`,
        method: "POST",
        auth: true,
      })
    );
  }

  markReviewed(payrollId: string): Promise<PayrollDto> {
    return this.apiHttpClient
      .request<null, PayrollDto>(
        HttpRequest.create({
          url: `${this.resourceUrl}/${payrollId}/mark-reviewed`,
          method: "POST",
          auth: true,
        })
      )
      .then((result: PayrollDto) => plainToInstance(PayrollDto, result));
  }

  markPending(payrollId: string): Promise<PayrollDto> {
    return this.apiHttpClient
      .request<null, PayrollDto>(
        HttpRequest.create({
          url: `${this.resourceUrl}/${payrollId}/mark-pending`,
          method: "POST",
          auth: true,
        })
      )
      .then((result: PayrollDto) => plainToInstance(PayrollDto, result));
  }

  recalculatePayrollsBulk(payload: { payrollIds?: string[]; request?: IRequest }): Promise<{ jobId: string }> {
    const query: string = payload.request ? new QueryRequestHelper(payload.request).getRequest().query() : "";
    return this.apiHttpClient.request<{ payrollIds?: string[] }, { jobId: string }>(
      HttpRequest.create({
        url: `${this.resourceUrl}/recalculate-bulk`,
        method: "POST",
        auth: true,
        query,
        body: { payrollIds: payload.payrollIds },
      })
    );
  }

  markReviewedBulk(payload: { payrollIds?: string[]; request?: IRequest }): Promise<{
    processedCount: number;
    skippedReviewedCount: number;
    totalCount: number;
  }> {
    const query: string = payload.request ? new QueryRequestHelper(payload.request).getRequest().query() : "";
    return this.apiHttpClient.request<
      { payrollIds?: string[] },
      { processedCount: number; skippedReviewedCount: number; totalCount: number }
    >(
      HttpRequest.create({
        url: `${this.resourceUrl}/mark-reviewed-bulk`,
        method: "POST",
        auth: true,
        query,
        body: { payrollIds: payload.payrollIds },
      })
    );
  }

  async downloadPayrollExcel(payload: { payrollId: string; excelName: string }): Promise<void> {
    await this.downloadExcel(null, `/${payload.payrollId}/excel`, payload.excelName);
  }

  getWorksheetsStatusByOperativeUnit(payload?: {
    from?: string;
    to?: string;
  }): Promise<WorksheetsStatusByOperativeUnitDto[]> {
    const searchParams = new URLSearchParams();
    if (payload?.from) searchParams.set("from", payload.from);
    if (payload?.to) searchParams.set("to", payload.to);

    const query: string = searchParams.toString();

    return this.apiHttpClient
      .request<null, WorksheetsStatusByOperativeUnitDto[]>(
        HttpRequest.create({
          url: `${this.resourceUrl}/worksheets-status-by-operative-unit`,
          method: "GET",
          auth: true,
          query: query.length ? query : null,
        })
      )
      .then((result) => plainToInstance(WorksheetsStatusByOperativeUnitDto, result));
  }
}
